export const SHIPPING_MODES = ["LTL", "FTL", "Box Truck", "Cargo Van", "Manual"];

// Brand logo — Premier Handling Solutions (transparent). Same asset everywhere:
// silver-outline look on dark backgrounds, clean black logo on light backgrounds.
import phsLogo from "@/assets/phs-logo.png";
export const LOGO_DARK = phsLogo;
export const LOGO_LIGHT = phsLogo;

export const LOCATION_TYPES = [
  "commercial",
  "residential",
  "limited access",
  "construction site",
  "farm",
  "government",
  "school",
  "warehouse",
];

export const FEDEX_SERVICE_TYPES = [
  { value: "FEDEX_FREIGHT_PRIORITY", label: "FedEx Freight Priority" },
  { value: "FEDEX_FREIGHT_ECONOMY", label: "FedEx Freight Economy" },
];

export const CUSTOMER_TYPES = ["standard", "contract", "broker", "wholesale", "government"];

export const QUOTE_STATUSES = ["draft", "quoted", "booked", "won", "lost", "expired"];

export const STATUS_COLORS = {
  draft: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  quoted: "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/40",
  booked: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40",
  won: "bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] border-[hsl(var(--success))]/50",
  lost: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40",
  expired: "bg-zinc-700/30 text-zinc-400 border-zinc-600/40",
};

export const ACCESSORIALS = [
  { key: "liftgate_pickup", label: "Liftgate Pickup" },
  { key: "liftgate_delivery", label: "Liftgate Delivery" },
  { key: "residential_pickup", label: "Residential Pickup" },
  { key: "residential_delivery", label: "Residential Delivery" },
  { key: "limited_access_pickup", label: "Limited Access Pickup" },
  { key: "limited_access_delivery", label: "Limited Access Delivery" },
  { key: "appointment_required", label: "Appointment Required" },
  { key: "inside_delivery", label: "Inside Delivery" },
  { key: "notify_before_delivery", label: "Notify Before Delivery" },
  { key: "hazmat", label: "Hazmat" },
  { key: "over_dimensional", label: "Over-Dimensional" },
];

export const CARRIER_LOGO_COLOR = {
  FEDEX_FREIGHT: "#4D148C",
  RL_CARRIERS: "#C8102E",
  WARP: "#0EA5E9",
  PRIORITY1: "#F47C20",
  MANUAL: "#71717A",
};

// Carrier brand directory: maps a carrier name (substring, lowercase) to its website domain
// (used for logo icons via Google's favicon service) and a brand color for the initials fallback.
// Covers our direct carriers + the common LTL carriers Priority1 returns.
export const CARRIER_BRANDS = [
  { match: "fedex", domain: "fedex.com", color: "#4D148C" },
  { match: "r+l", domain: "rlcarriers.com", color: "#C8102E" },
  { match: "rl carriers", domain: "rlcarriers.com", color: "#C8102E" },
  { match: "warp", domain: "wearewarp.com", color: "#0EA5E9" },
  { match: "saia", domain: "saia.com", color: "#0033A0" },
  { match: "daylight", domain: "dylt.com", color: "#F58220" },
  { match: "central transport", domain: "centraltransport.com", color: "#C8102E" },
  { match: "roadrunner", domain: "rrts.com", color: "#E11B22" },
  { match: "forward", domain: "forwardair.com", color: "#003DA5" },
  { match: "tforce", domain: "tforcefreight.com", color: "#5B2D90" },
  { match: "ward", domain: "wardtrucking.com", color: "#00529B" },
  { match: "dayton", domain: "daytonfreight.com", color: "#00563F" },
  { match: "old dominion", domain: "odfl.com", color: "#009639" },
  { match: "odfl", domain: "odfl.com", color: "#009639" },
  { match: "estes", domain: "estes-express.com", color: "#00529B" },
  { match: "averitt", domain: "averittexpress.com", color: "#C8102E" },
  { match: "duie pyle", domain: "aduiepyle.com", color: "#E31837" },
  { match: "southeastern", domain: "sefl.com", color: "#0072CE" },
  { match: "xpo", domain: "xpo.com", color: "#E4002B" },
  { match: "arcbest", domain: "arcbest.com", color: "#003087" },
  { match: "abf", domain: "abf.com", color: "#003087" },
  { match: "dohrn", domain: "dohrn.com", color: "#C8102E" },
  { match: "pitt", domain: "pittohio.com", color: "#003DA5" },
  { match: "holland", domain: "hollandregional.com", color: "#E31837" },
  { match: "new penn", domain: "newpenn.com", color: "#004B8D" },
  { match: "standard forwarding", domain: "standardfwd.com", color: "#0072CE" },
  { match: "magnum", domain: "magnumlog.com", color: "#231F20" },
  { match: "go2", domain: "go2delivery.com", color: "#1D4ED8" },
  { match: "priority1", domain: "priority1.com", color: "#F47C20" },
];

const _AVATAR_COLORS = ["#0F766E", "#7C3AED", "#B45309", "#0369A1", "#9D174D", "#15803D", "#9A3412", "#1D4ED8"];

function _hashColor(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return _AVATAR_COLORS[h % _AVATAR_COLORS.length];
}

// Resolve a carrier rate row to { domain, logo, color, initials } for icon rendering.
export function resolveCarrierBrand(rate) {
  const name = (rate.carrier_name || "").toLowerCase();
  const code = rate.carrier_code || "";
  const initials =
    (rate.carrier_name || "?")
      .replace(/[^a-zA-Z0-9 ]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";
  if (code === "MANUAL") return { domain: null, logo: null, color: "#71717A", initials: "MR" };
  const hit = CARRIER_BRANDS.find((b) => name.includes(b.match));
  const domain = hit ? hit.domain : null;
  return {
    domain,
    logo: domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : null,
    color: hit ? hit.color : _hashColor(rate.carrier_name || code),
    initials,
  };
}

export const INTEGRATION_SPECS = [
  {
    provider: "ghl",
    name: "GoHighLevel SMS",
    color: "#2ECC71",
    blurb: "Texts customers shipment updates (booked → in transit → delivered) and weather delay alerts from your GHL phone number. Requires an API 2.0 Private Integration token — legacy v1 API keys stopped working Dec 2025. In your GHL sub-account: Settings → Private Integrations → create one with 'View/Edit Contacts' and 'Edit Conversation Messages' scopes, then paste the pit-… token here.",
    fields: [
      { key: "api_key", label: "Private Integration Token (pit-…)", secret: true, required: true },
      { key: "location_id", label: "Location ID", required: true },
      { key: "from_number", label: "From Number (your GHL number)" },
    ],
  },
  {
    provider: "fedex",
    name: "FedEx Freight LTL",
    color: "#4D148C",
    blurb: "FedEx FREIGHT LTL only — fully separate from FedEx parcel/Express/Ground. Enter Client ID, Client Secret, and Account Number, then set Environment (use 'production' for live keys, 'preprod' for test keys). The correct FedEx servers are selected automatically from Environment.",
    fields: [
      { key: "client_id", label: "Freight Client ID", required: true },
      { key: "client_secret", label: "Freight Client Secret", secret: true, required: true },
      { key: "account_number", label: "Freight Account Number", required: true },
      { key: "environment", label: "Environment — 'production' for live keys, 'preprod' for test keys", required: true, help: "This single setting selects the correct FedEx auth + rating servers. Use 'production' with your live keys or 'preprod' with test/sandbox keys — no URLs to configure." },
      { key: "scope", label: "OAuth Scope", help: "For production keys this is usually 'fxf-api'. Leave blank only if Test Connection returns an 'invalid_scope' error." },
      { key: "test_mode", label: "Mock Mode (force PHS Estimate)", type: "toggle", help: "ON = skip the live Freight API and always return a PHS Estimate." },
      { key: "default_service_type", label: "Default Service Type", advanced: true },
      { key: "freight_account_number", label: "Billing Account # (optional)", advanced: true },
      { key: "address_line_1", label: "Account Address Line 1 (optional)", advanced: true },
      { key: "city", label: "Account City (optional)", advanced: true },
      { key: "state", label: "Account State (optional)", advanced: true },
      { key: "postal_code", label: "Account Postal Code (optional)", advanced: true },
    ],
  },
  {
    provider: "rl",
    name: "R+L Carriers",
    color: "#C8102E",
    blurb: "Secondary LTL rating source. Test Connection runs a real R+L Rate Quote API call.",
    fields: [
      { key: "api_key", label: "API Key", secret: true },
      { key: "account_number", label: "Account Number" },
      { key: "username", label: "Username" },
      { key: "password", label: "Password", secret: true },
      { key: "api_base_url", label: "API Base URL" },
      { key: "rate_quote_path", label: "Rate Quote Path" },
    ],
  },
  {
    provider: "warp",
    name: "Warp",
    color: "#0EA5E9",
    blurb: "LTL / FTL / box truck / cargo van broker. Fully configurable live integration with PHS Estimate fallback.",
    fields: [
      { key: "api_key", label: "API Key / Bearer Token", secret: true },
      { key: "api_base_url", label: "API Base URL" },
      { key: "quote_path", label: "Quote Endpoint Path" },
      { key: "timeout_seconds", label: "Request Timeout (sec)", type: "number" },
      { key: "test_mode", label: "Mock Mode (force PHS Estimate)", type: "toggle", help: "ON = skip the live API and always return a PHS Estimate." },
      { key: "error_logging", label: "Log Live API Errors", type: "toggle", default: true, help: "Store request payload + error details on the quote for troubleshooting." },
    ],
  },
  {
    provider: "hubspot",
    name: "HubSpot CRM",
    color: "#FF7A59",
    blurb: "Powers the Book Now tab — order # / confirmation lookup pulls the customer and ship-to straight from your CRM. Save your Private App access token (needs crm.objects.deals.read + crm.objects.contacts.read).",
    fields: [{ key: "access_token", label: "Private App Access Token", secret: true }],
  },
  {
    provider: "priority1",
    name: "Priority1",
    color: "#F47C20",
    blurb: "Multi-carrier LTL/TL aggregator. One call returns live rates from 15+ carriers; a strict 10% PHS admin margin is automatically baked into every Priority1 rate. Test Connection runs a real rate call. Rating only — booking always runs through R+L. (Truckload requires separate account enablement by Priority1.)",
    fields: [
      { key: "api_key", label: "API Key (X-API-KEY)", secret: true, required: true },
      { key: "api_base_url", label: "API Base URL", advanced: true },
    ],
  },
  {
    provider: "globaltranz",
    name: "GlobalTranz",
    color: "#00529B",
    blurb: "Multi-carrier LTL aggregator — one Rate V2 call returns live rates from dozens of carriers (shown as 'via GlobalTranz'), plus shipment booking (GTZ BOL number) and live tracking from Quote Detail. Auth: your GTZShip USERNAME + password and a customer access key from APIsupport@globaltranz.com. Dev environment: set the base URL to https://dev.gtzintegrate.com.",
    fields: [
      { key: "subscription_key", label: "Access Key (subscription-key)", secret: true, required: true, help: "Customer-specific access key issued by GlobalTranz API support." },
      { key: "username", label: "GTZShip Username", required: true, help: "Must be the account USERNAME — email logins do not work for the API." },
      { key: "password", label: "GTZShip Password", secret: true, required: true },
      { key: "carrierrate_username", label: "CarrierRate Portal Username", help: "Login for carrierrate.globaltranz.com — used to fetch the REAL Trifecta BOL PDF (the API has no document endpoint)." },
      { key: "carrierrate_password", label: "CarrierRate Portal Password", secret: true, help: "Password for the carrierrate.globaltranz.com portal login." },
      { key: "test_mode", label: "Mock Mode (skip live)", type: "toggle", help: "ON = skip the live GlobalTranz API entirely." },
      { key: "api_base_url", label: "API Base URL", advanced: true, help: "https://api.gtzintegrate.com (production) · https://dev.gtzintegrate.com (development)" },
      { key: "carrierrate_base_url", label: "CarrierRate Portal URL", advanced: true, help: "https://carrierrate.globaltranz.com" },
      { key: "timeout_seconds", label: "Timeout (seconds)", advanced: true },
    ],
  },
];
